import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';

import {
  ArrowLeft,
  Calendar,
  Clock,
  ShareNetwork,
  Tag,
  User
} from '@phosphor-icons/react';
const BlogDetail = () => {
  const { blogId } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/blogs/${blogId}`);
        if (!response.ok) {
          throw new Error('Blog not found');
        }
        const data = await response.json();
        setBlog(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [blogId, BACKEND_URL]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: blog.title,
          text: blog.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#6366F1]"></div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-[#161B26]">
        <LandingHeader />
        <div className="flex flex-col items-center justify-center py-32">
          <h1 className="text-4xl font-bold text-white mb-4">Blog Not Found</h1>
          <p className="text-[#A0AEC0] mb-8">The article you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/resources')}
            className="px-6 py-3 bg-[#6366F1] text-white rounded-lg hover:bg-[#5558E3] transition-colors"
          >
            Back to Resources
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#161B26]">
      <LandingHeader />
      
      {/* Hero Section */}
      <div className="relative">
        {blog.hero_image || blog.image_url ? (
          <div className="h-[400px] md:h-[500px] relative">
            <img
              src={
                (blog.hero_image || blog.image_url)?.startsWith('http') 
                  ? (blog.hero_image || blog.image_url)
                  : `${BACKEND_URL}${blog.hero_image || blog.image_url}`
              }
              alt={blog.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#161B26] via-[#161B26]/50 to-transparent"></div>
          </div>
        ) : (
          <div className="h-[200px] bg-gradient-to-br from-[#6366F1]/20 to-[#EC4899]/20"></div>
        )}
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 -mt-32 relative z-10 pb-20">
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Back Button */}
          <button
            onClick={() => navigate('/resources')}
            className="flex items-center gap-2 text-[#A0AEC0] hover:text-white transition-colors mb-8"
          >
            <ArrowLeft size={20} />
            Back to Resources
          </button>

          {/* Category Badge */}
          {blog.category && (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-[#6366F1] text-white mb-4">
              <Tag size={14} />
              {blog.category}
            </span>
          )}

          {/* Title */}
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            {blog.title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-6 text-[#A0AEC0] mb-8 pb-8 border-b border-white/10">
            <div className="flex items-center gap-2">
              <User size={18} />
              <span>{blog.author || 'Man & Machine Team'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar size={18} />
              <span>{formatDate(blog.created_at)}</span>
            </div>
            {blog.read_time && (
              <div className="flex items-center gap-2">
                <Clock size={18} />
                <span>{blog.read_time}</span>
              </div>
            )}
            <button
              onClick={handleShare}
              className="flex items-center gap-2 hover:text-[#6366F1] transition-colors ml-auto"
            >
              <ShareNetwork size={18} />
              ShareNetwork
            </button>
          </div>

          {/* Excerpt */}
          {blog.excerpt && (
            <p className="text-xl text-[#A0AEC0] mb-8 font-light leading-relaxed">
              {blog.excerpt}
            </p>
          )}

          {/* Main Content */}
          <div className="prose prose-invert prose-lg max-w-none">
            <ReactMarkdown
              components={{
                h1: ({ children }) => <h1 className="text-3xl font-bold text-white mt-10 mb-4">{children}</h1>,
                h2: ({ children }) => <h2 className="text-2xl font-bold text-white mt-8 mb-4">{children}</h2>,
                h3: ({ children }) => <h3 className="text-xl font-semibold text-white mt-6 mb-3">{children}</h3>,
                p: ({ children }) => <p className="text-[#A0AEC0] mb-4 leading-relaxed">{children}</p>,
                ul: ({ children }) => <ul className="list-disc list-inside text-[#A0AEC0] mb-4 space-y-2">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal list-inside text-[#A0AEC0] mb-4 space-y-2">{children}</ol>,
                li: ({ children }) => <li className="text-[#A0AEC0]">{children}</li>,
                strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
                a: ({ href, children }) => (
                  <a href={href} className="text-[#6366F1] hover:text-[#818CF8] underline" target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-[#6366F1] pl-4 italic text-[#A0AEC0] my-6">
                    {children}
                  </blockquote>
                ),
                code: ({ children }) => (
                  <code className="bg-white/10 px-2 py-1 rounded text-[#EC4899] text-sm">
                    {children}
                  </code>
                ),
              }}
            >
              {blog.content}
            </ReactMarkdown>
          </div>

          {/* CTA Section */}
          <div className="mt-12 p-8 bg-gradient-to-br from-[#6366F1]/20 to-[#EC4899]/20 rounded-2xl border border-white/10">
            <h3 className="text-2xl font-bold text-white mb-3">
              Ready to explore AI opportunities for your business?
            </h3>
            <p className="text-[#A0AEC0] mb-6">
              Start a free AI opportunity assessment and discover how AI can enhance your organization's productivity.
            </p>
            <a
              href="/register"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#6366F1] text-white rounded-lg hover:bg-[#5558E3] transition-colors font-semibold"
            >
              Start Free Assessment
              <ArrowLeft size={18} className="rotate-180" />
            </a>
          </div>
        </motion.article>
      </div>

      <Footer />
    </div>
  );
};

export default BlogDetail;
